// Detecting a new deploy while a tab is open.
//
// The built index.html loads a content-hashed entry chunk:
//   <script type="module" crossorigin src="/assets/index-<HASH>.js">
// That hash changes on every build, so it IS the deploy version.
// Comparing the hash the running document booted with against the hash
// in a freshly-fetched index.html tells us a new version was deployed —
// the exact condition behind the stale-chunk nav failure. Detecting it
// proactively lets us prompt for a reload BEFORE the user clicks into a
// route whose chunk no longer exists.
//
// In dev the entry is /src/main.ts (no hash), so parseEntryHash returns
// null and the whole feature no-ops (dev has HMR — no stale-deploy
// problem).

const ENTRY_RE = /\/assets\/index-([A-Za-z0-9_-]+)\.js/;

// parseEntryHash pulls the entry-chunk hash out of an index.html string
// (or a script src). Returns null when there's no hashed entry — dev
// mode, or a fetch that returned something unexpected.
export function parseEntryHash(html: string): string | null {
	const m = ENTRY_RE.exec(html);
	return m ? m[1] : null;
}

// currentEntryHash reads the hash of the entry script the live document
// actually booted with.
export function currentEntryHash(doc: Document): string | null {
	const el = doc.querySelector('script[type="module"][src*="/assets/index-"]');
	return parseEntryHash(el?.getAttribute('src') ?? '');
}

// isUpdateAvailable is true only when both hashes are known and differ.
// An unknown hash (dev, or a failed/garbled fetch) never trips a false
// prompt.
export function isUpdateAvailable(bootHash: string | null, latestHash: string | null): boolean {
	return !!bootHash && !!latestHash && bootHash !== latestHash;
}

// shouldPromptReload decides whether the "new version" prompt shows.
//
// Split out as a pure function because the bug it fixes was a state bug,
// not a detection bug: the prompt used to hang off a plain "dismissed"
// boolean, so closing it once silenced EVERY later deploy for the life of
// the tab. People then spent the day on stale code while being told their
// reports were fixed. Keyed on the dismissed BUILD instead, a newer build
// always gets to speak up again.
export function shouldPromptReload(
	bootHash: string | null,
	latestHash: string | null,
	dismissedHash: string | null,
): boolean {
	if (!isUpdateAvailable(bootHash, latestHash)) return false;
	return latestHash !== dismissedHash;
}

const DISMISSED_KEY = 'cos.update-dismissed-hash';

// The dismissed build outlives a reload (sessionStorage, per tab). Held only
// in memory it was forgotten by a reload, so a banner closed once came back
// on the next page load for the very same build. Storage can be blocked or
// absent, so every access is guarded and the in-memory value still works.
export function loadDismissedHash(): string | null {
	try {
		return window.sessionStorage.getItem(DISMISSED_KEY);
	} catch {
		return null;
	}
}

export function saveDismissedHash(hash: string | null): void {
	try {
		if (hash) window.sessionStorage.setItem(DISMISSED_KEY, hash);
		else window.sessionStorage.removeItem(DISMISSED_KEY);
	} catch {
		/* storage blocked: the in-memory dismissal still holds for this load */
	}
}
